<template>
    <div v-if="showControl" class="pregnancy-compact">
        <!-- Container compacto con toda la información -->
        <div class="pregnancy-container readonly">
            <!-- Fila superior: Indicador + FUR -->
            <div class="pregnancy-header">
                <!-- Indicador de control activo -->
                <div class="control-indicator">
                    <i class="fas fa-check-circle"></i>
                    <span class="control-text">Control de embarazo (de HC)</span>
                </div>

                <!-- Campo FUR (solo lectura) -->
                <div class="fur-compact">
                    <label>FUR</label>
                    <input type="text" :value="formatDate(furDate)" readonly class="fur-input-readonly">
                </div>
            </div>

            <!-- Resultados centrados (calculados según fecha de nota) -->
            <div v-if="gestationalAge" class="results-centered">
                <div class="result-item">
                    <i class="fas fa-hourglass-half"></i>
                    <span>{{ gestationalAge }}</span>
                </div>
                <div class="result-divider"></div>
                <div class="result-item">
                    <i class="fas fa-calendar-check"></i>
                    <span>FPP: {{ formatDate(dueDate) }}</span>
                </div>
                <div class="result-divider"></div>
                <div class="result-item">
                    <i class="fas fa-chart-line"></i>
                    <span>{{ trimester }}</span>
                </div>
                <div class="result-divider"></div>
                <div class="result-item note-date">
                    <i class="fas fa-calendar"></i>
                    <span>Fecha nota: {{ formatDate(noteDate) }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import api from '@/services/api';

const props = defineProps({
    patientRecord: {
        type: Object,
        required: true
    },
    noteDate: {
        type: String,
        required: true // Fecha de la nota médica (YYYY-MM-DD)
    }
});

const furDate = ref('');
const gestationalAge = ref('');
const dueDate = ref('');
const trimester = ref('');
const isPregnantInHC = ref(false);

const showControl = computed(() => {
    return isPregnantInHC.value && furDate.value;
});

const calculatePregnancyDetails = () => {
    if (!furDate.value || !props.noteDate) {
        gestationalAge.value = '';
        dueDate.value = '';
        trimester.value = '';
        return;
    }

    // Calcular edad gestacional desde FUR hasta la fecha de la nota
    const furDateObj = new Date(furDate.value);
    const noteDateObj = new Date(props.noteDate);
    const diffTime = Math.abs(noteDateObj - furDateObj);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(diffDays / 7);
    const days = diffDays % 7;

    gestationalAge.value = `${weeks} sem ${days} días`;

    // Calcular FPP (280 días desde FUR)
    const dueDateCalc = new Date(furDateObj);
    dueDateCalc.setDate(dueDateCalc.getDate() + 280);
    dueDate.value = dueDateCalc.toISOString().split('T')[0];

    // Determinar trimestre
    if (weeks < 13) {
        trimester.value = '1er Trimestre';
    } else if (weeks < 27) {
        trimester.value = '2do Trimestre';
    } else {
        trimester.value = '3er Trimestre';
    }
};

const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
};

const loadPregnancyData = async () => {
    try {
        const response = await api.get(`/pacientes/${props.patientRecord.id}`);
        const gynecologyData = response.data.data.gynecology;
        
        if (gynecologyData) {
            isPregnantInHC.value = Boolean(gynecologyData.is_pregnant);
            furDate.value = gynecologyData.last_menstrual_period || '';
            
            // Este componente calcula basado en la fecha de la nota (fecha histórica)
            // para mostrar cómo estaba el embarazo en ese momento específico
            if (isPregnantInHC.value && furDate.value) {
                calculatePregnancyDetails();
            }
        }
    } catch (error) {
        console.error('Error al cargar datos de embarazo:', error);
    }
};

// Watch para recalcular cuando cambia la fecha de la nota
watch(() => props.noteDate, () => {
    if (isPregnantInHC.value && furDate.value) {
        calculatePregnancyDetails();
    }
});

onMounted(() => {
    loadPregnancyData();
});
</script>

<style scoped>
/* Container compacto */
.pregnancy-compact {
    margin-bottom: 0.75rem;
}

/* Container principal con borde */
.pregnancy-container {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border: 2px solid #e9ecef;
    border-radius: 12px;
    padding: 1rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.pregnancy-container.readonly {
    background: linear-gradient(135deg, #f0f8ff 0%, #e6f3ff 100%);
    border-color: #b8d4f1;
}

/* Header */
.pregnancy-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.75rem;
}

/* Indicador de control activo */
.control-indicator {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #2563eb;
    font-weight: 600;
    font-size: 0.95rem;
}

.control-indicator i {
    font-size: 1.1rem;
}

/* Campo FUR compacto */
.fur-compact {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.fur-compact label {
    font-weight: 600;
    color: #495057;
    margin: 0;
    font-size: 0.9rem;
    white-space: nowrap;
}

.fur-input-readonly {
    padding: 0.4rem 0.75rem;
    border: 1px solid #ced4da;
    border-radius: 6px;
    background-color: #f8f9fa;
    font-size: 0.9rem;
    min-width: 100px;
    text-align: center;
    color: #495057;
}

/* Resultados centrados */
.results-centered {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
    padding-top: 0.75rem;
    border-top: 1px solid #dee2e6;
}

.result-item {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-weight: 600;
    color: #2c3e50;
    font-size: 0.95rem;
}

.result-item.note-date {
    color: #6c757d;
    font-size: 0.85rem;
}

.result-item i {
    color: #2563eb;
    font-size: 1rem;
}

.result-divider {
    width: 1px;
    height: 20px;
    background-color: #dee2e6;
}

/* Transiciones */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
    .pregnancy-header {
        flex-direction: column;
        align-items: stretch;
    }

    .results-centered {
        flex-direction: column;
        gap: 0.75rem;
    }

    .result-divider {
        display: none;
    }
}
</style>
